<template>
    <div class="container">
        <div role="button" class="small" @click="goBack()"><i
                class="fa-solid fa-chevron-left m-2 mt-3"></i>{{ $t('common.back') }}
        </div>
        <h3 class="my-3">{{ $t('nav.workload') }}</h3>
        <div class="d-flex justify-content-between align-items-center mb-3">
            <button class="btn btn-outline-primary" @click="goToPreviousMonth"><i class="fa-solid fa-chevron-left"></i></button>
            <div>{{ formatDate(currentDate, 'MMMM yyyy') }}</div>
            <button class="btn btn-outline-primary" @click="goToNextMonth"><i class="fa-solid fa-chevron-right"></i></button>
        </div>
        <div class="btn-group btn-group-sm w-100 mb-3" role="group">
            <template v-for="tab in tabs" :key="tab.value">
                <input type="radio" class="btn-check" name="workloadTab" :id="`workload-tab-${tab.value}`"
                    :value="tab.value" :checked="activeTab === tab.value" @change="setTab(tab.value)">
                <label class="btn btn-outline-primary" :for="`workload-tab-${tab.value}`">{{ $t(tab.labelKey) }}</label>
            </template>
        </div>
        <div v-if="loading">
        </div>
        <div v-else-if="activeTab === 'summary'">
            <div v-if="workload">
                <div v-for="item in workload" :key="item.id" class="card border-0 mb-3 shadow">
                    <div class="card-body">
                        <div class="row">
                            <div class="col h6">{{ item.name }}</div>
                            <div class="col text-end small">
                                <div role="button" @click="openModal(item.items)">{{ $t('workload.viewMore') }}<i class="fa-solid fa-chevron-right ms-3"></i></div>
                            </div>
                        </div>
                        <div class="row mt-3">
                            <div class="col"><span class="text-danger h3 me-1">{{ item.count }}</span> {{ $t('workload.entries') }}</div>
                            <div class="col"><span class="text-danger h3 me-1">{{ formatTotal(item.total) }}</span> {{ $t('workload.total') }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <NotFound v-else :item="$t('workload.reportItem')" />
        </div>
        <div v-else>
            <div v-if="hoursByDay && hoursByDay.days && hoursByDay.days.length" class="card border-0 mb-3 shadow small">
                <div class="row gx-1 px-2 py-2 border-bottom fw-semibold">
                    <div class="col-3">{{ $t('workload.colDate') }}</div>
                    <div class="col text-end">{{ $t('workload.colCount') }}</div>
                    <div class="col text-end">{{ $t('workload.colPoints') }}</div>
                    <div class="col text-end">{{ $t('workload.colWork') }}</div>
                    <div class="col text-end">{{ $t('workload.colDrive') }}</div>
                    <div class="col text-end">{{ $t('workload.colTotal') }}</div>
                </div>
                <template v-for="day in hoursByDay.days" :key="day.date">
                    <div role="button" class="row gx-1 px-2 py-2 border-bottom" @click="toggleDay(day.date)">
                        <div class="col-3 text-nowrap">
                            <i class="fa-solid fa-fw fa-xs me-1"
                                :class="openDays[day.date] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>{{ formatDate(parseISO(day.date), 'dd.MM') }}
                        </div>
                        <div class="col text-end">{{ day.count }}</div>
                        <div class="col text-end">{{ formatNumber(day.total_points) }}</div>
                        <div class="col text-end">{{ formatNumber(day.total) }}</div>
                        <div class="col text-end">{{ formatNumber(day.total_distance) }}</div>
                        <div class="col text-end fw-semibold">{{ formatNumber(day.total_hours) }}</div>
                    </div>
                    <template v-if="openDays[day.date]">
                        <router-link v-for="(row, index) in day.rows" :key="`${row.activity_id}-${index}`"
                            :to="`/tasks/${row.activity_id}`"
                            class="d-block px-2 py-2 border-bottom bg-body-tertiary text-decoration-none text-body">
                            <div class="ps-3">
                                <div class="fw-medium text-primary">{{ row.activity_name }}</div>
                                <div v-if="row.project_name" class="text-body-secondary">{{ row.project_name }}</div>
                            </div>
                            <div class="row gx-1">
                                <div class="col-3"></div>
                                <div class="col"></div>
                                <div class="col text-end">{{ formatNumber(row.total_points) }}</div>
                                <div class="col text-end">{{ formatNumber(row.total) }}</div>
                                <div class="col text-end">{{ formatNumber(row.total_distance) }}</div>
                                <div class="col text-end">{{ formatNumber(row.total_hours) }}</div>
                            </div>
                        </router-link>
                    </template>
                </template>
                <div v-if="hoursByDay.totals" class="row gx-1 px-2 py-2 fw-bold">
                    <div class="col-3">{{ $t('workload.colTotal') }}</div>
                    <div class="col text-end">{{ hoursByDay.totals.count }}</div>
                    <div class="col text-end">{{ formatNumber(hoursByDay.totals.total_points) }}</div>
                    <div class="col text-end">{{ formatNumber(hoursByDay.totals.total) }}</div>
                    <div class="col text-end">{{ formatNumber(hoursByDay.totals.total_distance) }}</div>
                    <div class="col text-end">{{ formatNumber(hoursByDay.totals.total_hours) }}</div>
                </div>
            </div>
            <NotFound v-else :item="$t('workload.reportItem')" />
        </div>
        <WorkloadModal :show="isModalVisible" :items="selectedItems" @close="isModalVisible = false" />
    </div>
</template>

<script>
import axios from '@/services/axios';
import NotFound from '@/components/NotFound.vue';
import WorkloadModal from '@/components/WorkloadModal.vue';
import { format, addMonths, parseISO } from 'date-fns';
import { dateFnsLocale, currentLocale } from '@/i18n';

export default {
    props: {
        yearMonth: {
            type: String,
            default: () => format(new Date(), 'yyyy-MM')
        }
    },
    data() {
        return {
            workload: null,
            hoursByDay: null,
            openDays: {},
            loading: true,
            isModalVisible: false,
            selectedItems: [],
            requestId: 0,
            tabs: [
                { value: 'summary', labelKey: 'workload.tabSummary' },
                { value: 'days', labelKey: 'workload.tabByDay' }
            ]
        };
    },
    components: {
        NotFound,
        WorkloadModal
    },
    computed: {
        activeTab() {
            return this.$route.query.tab === 'days' ? 'days' : 'summary';
        },
        currentDate() {
            const parsedDate = parseISO(`${this.yearMonth}-01`);
            return isNaN(parsedDate) ? new Date() : parsedDate;
        },
        // Everything a fetch depends on; one watcher refetches when any changes.
        fetchKey() {
            return [this.yearMonth, this.activeTab, this.$route.query.user_id].join('|');
        }
    },
    methods: {
        parseISO,
        async fetchWorkload() {
            const requestId = ++this.requestId;
            const params = { date: this.yearMonth };
            if (this.activeTab === 'days') params.report = 'hours_by_day';
            // Lets a privileged user view another user's report (e.g. ?user_id=2942).
            if (this.$route.query.user_id) params.user_id = this.$route.query.user_id;

            this.loading = true;
            this.workload = null;
            this.hoursByDay = null;
            this.openDays = {};
            try {
                const response = await axios.get(`/workload`, { params });
                if (requestId !== this.requestId) return;
                if (params.report) {
                    this.hoursByDay = response.data.data ?? response.data;
                } else {
                    this.workload = response.data.data;
                }
            } catch (error) {
                console.error('Error fetching workload details:', error);
            } finally {
                if (requestId === this.requestId) this.loading = false;
            }
        },
        setTab(tab) {
            this.$router.push({ query: { ...this.$route.query, tab: tab === 'days' ? 'days' : undefined } });
        },
        goBack() {
            if (this.$route.meta.internalNavigation && window.history.length > 2) {
                this.$router.go(-1);
            } else {
                this.$router.push('/');
            }
        },
        openModal(items) {
            this.selectedItems = items;
            this.isModalVisible = true;
        },
        goToPreviousMonth() {
            const previousMonth = format(addMonths(this.currentDate, -1), 'yyyy-MM');
            this.$router.push({ path: `/workload/${previousMonth}`, query: this.$route.query });
        },
        goToNextMonth() {
            const nextMonth = format(addMonths(this.currentDate, 1), 'yyyy-MM');
            this.$router.push({ path: `/workload/${nextMonth}`, query: this.$route.query });
        },
        formatDate(date, dateFormat) {
            return format(date, dateFormat, { locale: dateFnsLocale() });
        },
        // Summed hours arrive as floats (e.g. 7.129999999999999) — round to at
        // most 2 decimals without forcing trailing zeros.
        formatTotal(total) {
            const value = parseFloat(total);
            return isNaN(value) ? total : Math.round(value * 100) / 100;
        },
        // Fixed 2 decimals in the app locale (6,86 in ET) so the report columns
        // line up, matching the web report's number_format.
        formatNumber(value) {
            return (parseFloat(value) || 0).toLocaleString(currentLocale(), {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        toggleDay(date) {
            this.openDays = { ...this.openDays, [date]: !this.openDays[date] };
        }
    },
    watch: {
        fetchKey: {
            immediate: true,
            handler() {
                this.fetchWorkload();
            }
        }
    }
};
</script>
